import {
  type ResourceType,
  type Provider,
  type Condition,
  ConditionOperatorEnum,
} from '@repo/api-gw-sdk';
import type { ColumnDefBase } from '@tanstack/react-table';
import type React from 'react';

export function isCondition(value: unknown): value is Condition {
  return (
    typeof value === 'object' &&
    value !== null &&
    'operator' in value &&
    typeof value.operator === 'string'
  );
}

export function isValueCondition(condition: Condition | undefined): boolean {
  return !!condition && !isCombineCondition(condition);
}

export function isCombineCondition(condition: Condition | undefined): boolean {
  return (
    condition?.operator === ConditionOperatorEnum.And ||
    condition?.operator === ConditionOperatorEnum.Or
  );
}

export const createCombineCondition = (
  conditions: Condition[] = [],
  operator = ConditionOperatorEnum.And
): Condition => ({
  operator,
  conditions,
});

export const createValueCondition = (
  property: string,
  value: string[]
): Condition => ({
  property,
  operator: ConditionOperatorEnum.ContainsAnyOf,
  value,
});

export function createFromCondition(
  property: string,
  value: string
): Condition {
  return {
    operator: ConditionOperatorEnum.From,
    property,
    value: [value],
  };
}

export function createToCondition(property: string, value: string): Condition {
  return {
    operator: ConditionOperatorEnum.To,
    property,
    value: [value],
  };
}

export const createAllIncludedCondition = (): Condition => ({
  property: '',
  operator: '',
  conditions: [],
  value: ['true'],
});

export const createAllExcludedCondition = (): Condition => ({
  property: '',
  operator: '',
  conditions: [],
  value: ['false'],
});

export function collectValueConditions(condition: Condition): Condition[] {
  return isValueCondition(condition)
    ? [condition]
    : condition.conditions?.flatMap(collectValueConditions) || [];
}

export function findValueCondition(
  conditions: Condition[],
  propertyName: string | undefined
): Condition | undefined {
  return conditions.find(
    (x) => isValueCondition(x) && x.property === propertyName
  );
}

export function conditionValues(condition: Condition | undefined): string[] {
  return isValueCondition(condition) ? (condition?.value ?? []) : [];
}

export enum PropertyPolicyRelation {
  SingleValue = 'SingleValue',
  MultipleValues = 'MultipleValues',
  /** Genuinely string-only: Contains/StartsWith/EndsWith and their negations, no In/NotIn. */
  String = 'String',
  /** Offers both "pick an exact value" (In/NotIn) and free-text string operators — resourceName is the one property shaped this way. */
  SingleValueOrString = 'SingleValueOrString',
  /**
   * A number compared against a single threshold. The condition still stores its
   * value as a one-element string array like every other, so the editor owns
   * parsing it back — nothing downstream knows the string holds a number.
   */
  Numeric = 'Numeric',
}

interface FieldComponentProps<T> {
  entity: T;
  onChange?: () => Promise<void>;
}

/**
 * Set by a host that renders a condition inside a popover it owns (the filter
 * strip anchors one to each chip). Its presence — not a set of independent
 * boolean props — is what puts a condition component in hosted mode: no trigger
 * button of its own, open from mount, anchored to the host, and no free-text
 * "add new" affordance. Absent when the condition renders inline, as in the
 * condition builder.
 */
export interface ConditionPanelHost {
  /**
   * The mounted element to anchor to, not a ref to it: MUI's Popover wants an
   * element, and a ref would have to be read during render — where it is still
   * null on the commit the popover mounts in, and mutating it later does not
   * re-render.
   */
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

/** One selectable value, shared by every filter and condition selection surface. */
export interface FilterOption {
  id: string;
  name?: string;
  Image?: React.ReactNode;
  description?: React.ReactNode;
  isGroupTitle?: boolean;
  /** Set when the Image already carries the label, so the name isn't printed twice. */
  hideName?: boolean;
}

export interface MultiSelectConditionPanelProps {
  property: FilterProperty;
  entityName: string;
  /**
   * Names what the selection count holds, for properties whose full entity name reads as a reminder there —
   * "2 types selected" under a "Key management type" filter. Defaults to `entityName`.
   */
  selectionEntityName?: string;
  isLoading: boolean;
  options: FilterOption[];
  condition: Condition;
  onChange: (condition: Condition) => void;
  showAddNewButton: boolean;
  showSearchBox: boolean;
  useImagesInSelectedView?: boolean;
  showNameFirst?: boolean;
  shouldShowId?: boolean;
  showSelectAll?: boolean;
  panelHost?: ConditionPanelHost;
  /** Runs whenever the popover closes, hosted or not — for the wrapper's own cleanup. */
  onPanelClose?: () => void;
  onSearchChange?: (value: string) => void;
}

export interface FilterProps {
  property: FilterProperty;
  conditions: Condition[];
  'data-testid'?: string;
  onChange: (conditions: Condition[]) => void;
}

/**
 * Which value set a condition offers. The filter strip narrows a property to the
 * values present in the current inventory ('discovered'); every other surface —
 * the condition editor for policies and notifications — offers all possible
 * values ('all'), since those can target values not yet discovered.
 */
type ConditionOptionScope = 'discovered' | 'all';

/**
 * A condition editor for a property that owns several conditions at once — see
 * {@link FilterProperty.condition.companion}. It edits the whole set, so it
 * takes the plural where an ordinary condition component takes the singular.
 */
export interface CompanionConditionComponentProps {
  conditions: Condition[];
  onChange: (conditions: Condition[]) => void;
  property: FilterProperty;
  panelHost?: ConditionPanelHost;
}

/** Supplies a condition's options; may read the condition props and fetch. */
export type ConditionOptionsHook = (
  props: MultiSelectConditionComponentProps
) => {
  options: FilterOption[];
  isLoading?: boolean;
};

/**
 * An option source a surface can also ask "are there any at all". Reporting
 * whether it is still loading is required rather than optional here, because the
 * answer is read the moment loading is over: a source that never says so is
 * taken at its word while empty, and its property disappears from the
 * add-filter menu until the values arrive.
 */
export type PropertyOptionsHook = (
  props: MultiSelectConditionComponentProps
) => {
  options: FilterOption[];
  isLoading: boolean;
};

export interface MultiSelectConditionComponentProps {
  condition: Condition;
  onChange: (condition: Condition) => void;
  property: FilterProperty;
  supportedProviders?: Provider[];
  supportedResourceTypes?: ResourceType[];
  panelHost?: ConditionPanelHost;
  /** Defaults to 'all' when unset. See {@link ConditionOptionScope}. */
  optionScope?: ConditionOptionScope;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface FilterProperty<T = any> {
  name: string;
  displayName: string;
  /**
   * Which operators a condition on this property offers. Omitted means
   * single-select, and it must name the same shape as `type` — the two are read
   * by different surfaces, so disagreeing leaves a chip opening on an operator
   * its own picker does not list.
   */
  policyRelation?: PropertyPolicyRelation;
  propertyPredicate?: (providers: Provider[]) => boolean;
  group: string;
  column?: ColumnDefBase<T>;
  accessorFn?: (row: T) => unknown;
  field?: {
    getTitle?: (entity: T) => string;
    Component: (props: FieldComponentProps<T>) => React.ReactNode;
  };
  condition?: {
    /**
     * Label used wherever the property is named as a filter — the condition
     * builder (PropertySelect / ConditionViewer) and the filter strip's chips.
     * Falls back to the property `displayName` when omitted. Use this when
     * filtering needs a more explicit label than the table column.
     */
    displayName?: string;
    /**
     * Groups this property under a fly-out sub-menu in the condition property
     * picker (PropertySelect). Properties sharing the same `group` collapse
     * into a single parent entry (e.g. "Resource tags" → "Tag keys" /
     * "Tag key-value pairs"). `label` is the short text shown inside the
     * sub-menu; the full condition label still comes from
     * `condition.displayName` / `displayName`.
     */
    subMenu?: {
      group: string;
      label: string;
    };
    Component: (props: MultiSelectConditionComponentProps) => React.ReactNode;
    /**
     * A second editor for surfaces that can host one chip owning several
     * conditions — today the filter strip. Data classes needs it because
     * ticking a whole class writes `dataClasses` while ticking an entity inside
     * it writes `dataClassEntities`; `properties` names those companions.
     * Surfaces that edit one condition at a time keep using `Component`.
     */
    companion?: {
      properties: string[];
      Component: (props: CompanionConditionComponentProps) => React.ReactNode;
    };
    /**
     * Where this property's selectable values come from. The editor renders them
     * and the filter strip reads the same hook to find out whether there are any
     * at all — a property with none is dropped from the add-filter menu rather
     * than offering a panel that can only come up empty. One source, so the menu
     * cannot disagree with the panel it opens.
     */
    useOptions?: PropertyOptionsHook;
    /**
     * For a property whose stored values are not the ones the user picks — a
     * backup policy's "Standard" stands for three backend policy types — this
     * translates between the two, and everything that reads the condition goes
     * through it: the editor is handed picked values, its edits are stored back
     * as stored values, and the chip counts what was picked rather than what
     * that expanded to. One mapping, so those three cannot drift apart.
     */
    valueLens?: ConditionValueLens;
  };
}

interface ConditionValueLens {
  /** Stored values as the user picked them. */
  toPicked: (stored: string[]) => string[];
  /** Picked values as they are stored on the condition. */
  toStored: (picked: string[]) => string[];
}

/**
 * A lens belongs to one property, so it is applied only to that property's own
 * condition — a chip carrying a companion's condition alongside, or one no
 * property claims, passes through untouched.
 */
const lensFor = (condition: Condition, property: FilterProperty | undefined) =>
  condition.property === property?.name
    ? property?.condition?.valueLens
    : undefined;

/** What the user picked to produce this condition. */
export const pickedConditionValues = (
  condition: Condition,
  property: FilterProperty | undefined
) => {
  const values = condition.value ?? [];
  return lensFor(condition, property)?.toPicked(values) ?? values;
};

/** The condition as its editor reads it, which is in picked values. */
export const asPickedCondition = (
  condition: Condition,
  property: FilterProperty | undefined
): Condition =>
  lensFor(condition, property)
    ? { ...condition, value: pickedConditionValues(condition, property) }
    : condition;

/** An edited condition on its way back to being stored. */
export const asStoredCondition = (
  condition: Condition,
  property: FilterProperty | undefined
): Condition => {
  const lens = lensFor(condition, property);
  return lens
    ? { ...condition, value: lens.toStored(condition.value ?? []) }
    : condition;
};

export const isAnAllCondition = (condition: Condition) => {
  return isEmptyCondition(condition) && condition.value?.length === 1;
};

export const isEmptyCondition = (condition: Condition) => {
  return (
    !condition.property && !condition.operator && !condition.conditions?.length
  );
};

export const hasValidConditionValues = (condition?: Condition): boolean => {
  if (!condition) {
    return false;
  }

  if (condition.value !== undefined) {
    return !!condition.value.length;
  }

  if (condition.conditions?.length) {
    return condition.conditions.every(hasValidConditionValues);
  }

  return false;
};
